import React, { useEffect, useState } from 'react';
import { FaEnvelope, FaSms } from 'react-icons/fa';

import axios from 'helpers/axios';
import config from 'helpers/config';

interface Props {
    inspectionId: string;
    onClose: () => void;
}

interface Contacts {
    email: string | null;
    phone: string | null;
}

const SendInspectionModal: React.FC<Props> = ({ inspectionId, onClose }) => {
    const [contacts, setContacts] = useState<Contacts>({ email: null, phone: null });
    const [sendEmail, setSendEmail] = useState(false);
    const [sendSms, setSendSms] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [sentResult, setSentResult] = useState<{ emailSent: boolean; smsSent: boolean } | null>(null);

    useEffect(() => {
        axios
            .get(`${config.apiUrl}/inspection/${inspectionId}/contacts`)
            .then((res) => {
                setContacts(res.data);
                setSendEmail(!!res.data.email);
                setSendSms(!!res.data.phone);
            })
            .catch(() => undefined)
            .finally(() => setIsLoading(false));
    }, [inspectionId]);

    const send = () => {
        setIsSending(true);
        axios
            .post(`${config.apiUrl}/inspection/${inspectionId}/send`, { sendEmail, sendSms })
            .then((res) => setSentResult(res.data))
            .catch(() => setIsSending(false));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl p-6 w-96" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-semibold text-lg text-gray-800 mb-4">Send inspection to customer</h3>

                {isLoading ? (
                    <div className="text-gray-500 text-center py-4">Loading…</div>
                ) : sentResult ? (
                    <div className="text-center py-4">
                        <div className="text-green-600 font-semibold mb-2">Sent!</div>
                        <div className="text-sm text-gray-600 space-y-1">
                            {sentResult.emailSent && <div>✓ Email to {contacts.email}</div>}
                            {sentResult.smsSent && <div>✓ Text to {contacts.phone}</div>}
                            {!sentResult.emailSent && !sentResult.smsSent && (
                                <div className="text-yellow-700">Nothing was sent — check the contact details.</div>
                            )}
                        </div>
                        <button
                            className="mt-4 px-4 py-2 rounded-md bg-blue-600 text-white font-semibold clickable hover:bg-blue-700"
                            onClick={onClose}
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="space-y-3 mb-6">
                            <label className={`flex items-center ${contacts.email ? 'clickable' : 'opacity-50'}`}>
                                <input
                                    type="checkbox"
                                    disabled={!contacts.email}
                                    checked={sendEmail}
                                    onChange={(e) => setSendEmail(e.target.checked)}
                                    className="mr-3"
                                />
                                <FaEnvelope className="mr-2 text-gray-500" />
                                <span className="font-medium">Email</span>
                                <span className="ml-auto text-sm text-gray-500">
                                    {contacts.email || 'No email on file'}
                                </span>
                            </label>
                            <label className={`flex items-center ${contacts.phone ? 'clickable' : 'opacity-50'}`}>
                                <input
                                    type="checkbox"
                                    disabled={!contacts.phone}
                                    checked={sendSms}
                                    onChange={(e) => setSendSms(e.target.checked)}
                                    className="mr-3"
                                />
                                <FaSms className="mr-2 text-gray-500" />
                                <span className="font-medium">Text message</span>
                                <span className="ml-auto text-sm text-gray-500">
                                    {contacts.phone || 'No number on file'}
                                </span>
                            </label>
                        </div>
                        <div className="flex">
                            <button
                                className="flex-1 mr-2 py-2 rounded-md text-blue-500 font-semibold clickable hover:bg-blue-50"
                                onClick={onClose}
                            >
                                Cancel
                            </button>
                            <button
                                disabled={isSending || (!sendEmail && !sendSms)}
                                className="flex-1 py-2 rounded-md bg-blue-600 text-white font-semibold clickable hover:bg-blue-700 disabled:opacity-50"
                                onClick={send}
                            >
                                Send
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SendInspectionModal;
